import { translateTextBatch } from '../hooks/useUniversalTranslation';

const RTL_LANGUAGES = new Set(['ur', 'ps', 'ar', 'fa', 'prs']);

const detectSource = (row, fields) => {
    const declared = String(row?.original_language || 'en').toLowerCase();
    const text = fields.map(field => row?.[field] || '').join(' ');
    const arabicScript = /[\u0600-\u06ff]/.test(text);
    if (arabicScript) return RTL_LANGUAGES.has(declared) ? declared : 'ur';
    return RTL_LANGUAGES.has(declared) ? 'en' : declared;
};

async function translateCollection(rows, fields, targetLanguage, signal) {
    const result = rows.map(row => ({ ...row }));
    const grouped = new Map();

    result.forEach((row, rowIndex) => {
        const serverFields = row?._translation?.target === targetLanguage
            ? new Set(row._translation.fields || [])
            : new Set();
        fields.forEach(field => {
            if (serverFields.has(field)) return;
            const text = String(row?.[field] ?? '').trim();
            if (!text) return;
            const source = detectSource(row, [field]);
            if (!targetLanguage || source === targetLanguage) return;
            if (!grouped.has(source)) grouped.set(source, []);
            grouped.get(source).push({ rowIndex, field, text });
        });
    });

    await Promise.all([...grouped.entries()].map(async ([source, entries]) => {
        const unique = [...new Set(entries.map(entry => entry.text))];
        const translated = await translateTextBatch(unique, targetLanguage, source, { signal });
        const lookup = new Map(unique.map((text, index) => [text, translated[index] || text]));
        entries.forEach(({ rowIndex, field, text }) => {
            const value = lookup.get(text) || text;
            if (value === text) return;
            const row = result[rowIndex];
            row._original = { ...(row._original || {}), [field]: row[field] };
            row[field] = value;
            row._translation = {
                source,
                target: targetLanguage,
                fields: [...new Set([...(row._translation?.fields || []), field])]
            };
        });
    }));

    return result;
}

export async function translateSavedRows(rows, fields, targetLanguage, options = {}) {
    const sourceRows = Array.isArray(rows) ? rows : [];
    const translated = await translateCollection(sourceRows, fields, targetLanguage, options.signal);
    for (const nested of options.nested || []) {
        await Promise.all(translated.map(async row => {
            const children = Array.isArray(row[nested.key]) ? row[nested.key] : [];
            row[nested.key] = await translateCollection(children, nested.fields, targetLanguage, options.signal);
        }));
    }
    return translated;
}
